import React from 'react';
import { usePortal } from '../PortalContext';
import { UDA_THEMES } from '../udaThemes';

const aboutLinks = [
  { label: 'Vision & Mission', href: '/about' },
  { label: 'Organizational Structure', href: '/about/team' },
  { label: "Director's Message", href: '/about' },
  { label: 'History', href: '/about' },
  { label: 'Annual Reports', href: '/resources' },
  { label: 'Organogram', href: '/about/team' },
];

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'Acts & Rules', href: '/resources/acts-bylaws' },
  { label: 'GO & Orders', href: '/resources/gos-circulars' },
  { label: 'Master Plans', href: '/resources' },
  { label: 'Tenders', href: '/resources' },
  { label: 'Circulars', href: '/resources/gos-circulars' },
];

const serviceLinks = [
  { label: 'Building Permissions', href: '/services' },
  { label: 'Layout Approvals', href: '/services' },
  { label: 'Land Use Certificate', href: '/services' },
  { label: 'Regularization', href: '/services' },
  { label: 'Grievance Portal', href: '/help-desk' },
  { label: 'RTI Portal', href: '/rti' },
];

const legalLinks = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Disclaimer', href: '/terms-of-service' },
  { label: 'Accessibility', href: '/accessibility' },
  { label: 'Website Policy', href: '/terms-of-service' },
  { label: 'Terms of Use', href: '/terms-of-service' },
  { label: 'Copyright Policy', href: '/terms-of-service' },
];

const socials = [
  { icon: 'ri-facebook-fill', label: 'Facebook' },
  { icon: 'ri-twitter-x-fill', label: 'Twitter' },
  { icon: 'ri-youtube-fill', label: 'YouTube' },
  { icon: 'ri-instagram-fill', label: 'Instagram' },
  { icon: 'ri-linkedin-fill', label: 'LinkedIn' },
];

const FooterLinkColumn: React.FC<{ title: string; links: { label: string; href: string }[] }> = ({ title, links }) => (
  <div>
    <h4 className="text-[11px] font-bold text-white uppercase tracking-[0.16em] mb-4 flex items-center gap-2">
      {title}
      <span className="h-px flex-1 bg-white/10" />
    </h4>
    <ul className="flex flex-col gap-2.5">
      {links.map((link, i) => (
        <li key={i}>
          <a
            href={link.href}
            className="text-[13px] text-gray-400 hover:text-white transition-colors duration-200 inline-flex items-center gap-1.5 group"
          >
            <i className="ri-arrow-right-s-line text-[11px] text-gray-600 group-hover:text-[color:var(--portal-accent-light)] transition-colors duration-200" />
            <span className="group-hover:translate-x-0.5 transition-transform duration-200">{link.label}</span>
          </a>
        </li>
      ))}
    </ul>
  </div>
);

export const PortalFooter: React.FC = () => {
  const { siteId } = usePortal();
  // Footer brand shows the UDA code the portal is scoped to (e.g. "AMUDA").
  // The DTCP main site has no UDA_THEMES entry, so it keeps the "DTCP" code.
  const udaCode = Object.values(UDA_THEMES).find((t) => t.id === siteId)?.code || 'DTCP';

  return (
  <footer className="relative overflow-hidden bg-[#0E1117] text-gray-300">
    {/* Accent hairline capping the footer, mirroring the tricolour rule under
        the header — the page opens and closes on the same device. */}
    <span
      aria-hidden="true"
      className="absolute inset-x-0 top-0 h-[3px]"
      style={{
        background:
          'linear-gradient(90deg, var(--portal-accent-dark), var(--portal-accent) 45%, var(--portal-accent-light))',
      }}
    />

    {/* Giant faint UDA-code watermark behind the footer, like the RUDA portal. */}
    <div
      aria-hidden="true"
      className="pointer-events-none select-none absolute inset-0 flex items-center justify-center overflow-hidden"
    >
      <span className="font-extrabold tracking-tighter leading-none text-white/[0.04] text-[30vw] lg:text-[22vw]">
        {udaCode}
      </span>
    </div>

    {/* Ambient accent bloom, bottom-left */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -bottom-40 -left-32 w-[34rem] h-[34rem] rounded-full blur-3xl opacity-[0.10]"
      style={{ background: 'var(--portal-accent)' }}
    />

    {/* Main Footer */}
    <div className="relative z-10 portal-container pt-14 pb-10">
      <div className="grid grid-cols-2 lg:grid-cols-[1.6fr_1fr_1fr_1fr_1fr] gap-8 sm:gap-10 items-start">
        {/* DTCP Info */}
        <div className="col-span-2 lg:col-span-1">
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, var(--portal-accent), var(--portal-accent-dark))',
                boxShadow: '0 8px 20px -8px var(--portal-accent)',
              }}
            >
              <i className="ri-government-fill text-white text-xl" />
            </div>
            <div>
              <h4 className="text-[15px] font-extrabold text-white tracking-tight">{udaCode}</h4>
              <p className="text-[11px] text-gray-500 tracking-wide">Andhra Pradesh</p>
            </div>
          </div>
          <p className="text-[13px] text-gray-500 leading-[1.75] mb-5">
            Directorate of Town &amp; Country Planning, Government of Andhra Pradesh. Regulating urban development and ensuring planned growth since 1920.
          </p>
          <div className="flex flex-col gap-3 text-[13px] text-gray-400 mb-6">
            <div className="flex items-start gap-2.5">
              <i className="ri-map-pin-2-fill text-sm mt-0.5 flex-shrink-0" style={{ color: 'var(--portal-accent)' }} />
              <span>DTCP Office, Mangalagiri, Guntur District, AP - 522503</span>
            </div>
            <a href="tel:08632000662" className="flex items-center gap-2.5 hover:text-white transition-colors duration-200">
              <i className="ri-phone-fill text-sm flex-shrink-0" style={{ color: 'var(--portal-accent)' }} />
              <span>0863-2000662</span>
            </a>
            <a href="mailto:dtcp-ap@ap.gov.in" className="flex items-center gap-2.5 hover:text-white transition-colors duration-200">
              <i className="ri-mail-fill text-sm flex-shrink-0" style={{ color: 'var(--portal-accent)' }} />
              <span>dtcp-ap@ap.gov.in</span>
            </a>
          </div>

          {/* Social Icons */}
          <div className="flex items-center gap-2">
            {socials.map((s, i) => (
              <a
                key={i}
                href="#"
                aria-label={s.label}
                className="group w-9 h-9 flex items-center justify-center bg-white/[0.06] ring-1 ring-white/10 rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:ring-transparent"
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--portal-accent)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
              >
                <i className={`${s.icon} text-sm text-gray-400 group-hover:text-white transition-colors duration-200`} />
              </a>
            ))}
          </div>
        </div>

        <FooterLinkColumn title="About" links={aboutLinks} />
        <FooterLinkColumn title="Quick Links" links={quickLinks} />
        <FooterLinkColumn title="Services" links={serviceLinks} />
        <FooterLinkColumn title="Legal" links={legalLinks} />
      </div>
    </div>

    {/* Bottom Bar */}
    <div className="relative z-10 border-t border-white/[0.07]">
      <div className="portal-container py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11.5px] text-gray-500 text-center sm:text-left">
        <span>&copy; {new Date().getFullYear()} DTCP AP. All Rights Reserved.</span>
        <div className="flex items-center gap-3">
          {/* Tricolour tick — closes the page on the same national mark the
              header opens with. */}
          <span aria-hidden="true" className="hidden sm:flex h-[3px] w-9 rounded-full overflow-hidden">
            <span className="flex-1 bg-[#FF9933]" />
            <span className="flex-1 bg-white" />
            <span className="flex-1 bg-[#138808]" />
          </span>
          <span>Powered by Dreamstep Software Innovations Pvt. Ltd.</span>
        </div>
      </div>
    </div>
  </footer>
  );
};
